import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Paginator } from "primereact/paginator";
import * as Yup from "yup";
import { GetMasterTypeData } from "../../../Services/Global-Services/GlobalServices";
import {
  getArrearList,
  postArrearDelete,
  postArrearUpdate,
} from "./Services/PayrollService";
import { getEmployeeList } from "../EmployeeList/Service/employeeService";
import { TostMsg } from "../../../App";

// ---------- helpers ----------
const rup = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : "₹" + Number(n).toLocaleString("en-IN");

// The API wants the numeric employee id. Your old code read `EmpId` from the
// employee object, so prefer that and fall back to `Id`. Change here if needed.
const empKey = (e) => e?.EmpId ?? e?.Id;
const findEmployee = (list, ref) =>
  list.find(
    (e) =>
      String(empKey(e)) === String(ref) ||
      String(e.Id) === String(ref) ||
      String(e.EmploymentId) === String(ref),
  );

const TAX_OPTIONS = ["Taxable", "Non-Taxable"];

const EMPTY_FORM = {
  EmpId: "",
  ArrearTypeId: "",
  ForMonth: "",
  Amount: "",
  ArrearStatusId: "",
  Taximpact: "Taxable",
  Reason: "",
};

const FieldError = ({ message }) =>
  message ? (
    <small style={{ color: "var(--danger)", display: "block", marginTop: "4px" }}>
      {message}
    </small>
  ) : null;

const arrearValidationSchema = Yup.object().shape({
  EmpId: Yup.string().required("Employee is required"),
  ArrearTypeId: Yup.string().required("Arrear type is required"),
  ForMonth: Yup.string().required("Month is required"),
  Amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .positive("Amount must be greater than 0")
    .max(10000000, "Amount cannot exceed ₹1,00,00,000")
    .test(
      "two-decimals",
      "Use at most 2 decimal places",
      (v) => v === undefined || Math.round(v * 100) / 100 === v,
    ),
  ArrearStatusId: Yup.string().required("Status is required"),
  Taximpact: Yup.string()
    .required("Tax impact is required")
    .oneOf(TAX_OPTIONS, "Select a valid tax impact"),
  Reason: Yup.string().max(500, "Reason cannot exceed 500 characters"),
});

const ArrearManagement = () => {
  const toaster = useContext(TostMsg);
  const userDetails = useMemo(
    () => JSON.parse(localStorage.getItem("User") || "{}"),
    [],
  );

  // lookups
  const [arrearStatusList, setArrearStatusList] = useState([]);
  const [arrearTypesList, setArrearTypesList] = useState([]);
  const [formonthList, setFormonthList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  // list
  const [summary, setSummary] = useState(null);
  const [arrears, setArrears] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const searchTimeout = useRef(null);

  // modals / form
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editArrear, setEditArrear] = useState(null);
  const [deleteArrear, setDeleteArrear] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ---------- loading ----------
  useEffect(() => {
    loadMasterData();
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMasterData = async () => {
    try {
      const base = { allRecords: true, organisationId: userDetails?.OrgId };
      const [statusRes, typeRes, monthRes] = await Promise.all([
        GetMasterTypeData({ ...base, masterType: "ArrearStatus" }),
        GetMasterTypeData({ ...base, masterType: "ArrearTypes" }),
        GetMasterTypeData({ ...base, masterType: "formonth" }),
      ]);
      setArrearStatusList(statusRes?.Data || []);
      setArrearTypesList(typeRes?.Data || []);
      setFormonthList(monthRes?.Data || []);
    } catch (error) {
      console.error("Error loading master data:", error);
      toaster?.current?.error("Could not load arrear master data.");
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await getEmployeeList({
        OrganisationId: userDetails?.OrgId,
        LoggedInEmployeeId: userDetails?.EpmId,
        IsDirectReportOrHierarchy: 0,
        EmployeeId: 0,
        SearchText: "",
        FollowersOnly: 0,
        FollowingsOnly: 0,
        IsSuggestedOnly: 0,
        IsExEmployeesAlso: 0,
        pageNumber: 1,
        pageSize: 10000,
      });
      setEmployeeList(res?.Data || []);
    } catch (error) {
      console.error("Error fetching employee list:", error);
      toaster?.current?.error("Could not load employee list.");
    }
  };

  const fetchArrears = async (page = 1, size = rows) => {
    setIsLoading(true);
    try {
      const res = await getArrearList({
        ArrearId: 0,
        OrgId: userDetails?.OrgId,
        GUID: "",
        EmpId: 0,
        Search: searchQuery || "",
        ArrearStatusId: filterStatus || "",
        ArrearTypeId: filterType || "",
        LoggedInEmpId: 0,
        PageNumber: page,
        PageSize: size,
      });
      const items = res?.Data?.ArrearResponseDetails || [];
      setSummary(res?.Data?.ArrearDashboardDetails?.[0] || null);
      setArrears(items);
      setTotalRecords(
        res?.Data?.RecordCount ??
          res?.Data?.TotalRecords ??
          res?.Data?.TotalCount ??
          items.length,
      );
    } catch (error) {
      console.error("Error fetching arrear list:", error);
      toaster?.current?.error("Could not load arrears. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFirst(0);
      fetchArrears(1, rows);
    }, 500);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterType, filterStatus]);

  const currentPage = () => Math.floor(first / rows) + 1;

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    fetchArrears(event.first / event.rows + 1, event.rows);
  };

  // ---------- validation ----------
  const validateField = async (name, data = formData) => {
    try {
      await arrearValidationSchema.validateAt(name, data);
      setFormErrors((p) => ({ ...p, [name]: "" }));
    } catch (err) {
      setFormErrors((p) => ({ ...p, [name]: err.message }));
    }
  };

  const validateForm = async () => {
    try {
      await arrearValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => {
        if (!errors[e.path]) errors[e.path] = e.message;
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };
    setFormData(next);
    if (formErrors[name]) validateField(name, next);
  };

  const handleBlur = (e) => validateField(e.target.name);

  const applyServerErrors = (res) => {
    const serverErrors = res?.Errors || res?.errors;
    if (!serverErrors || typeof serverErrors !== "object") return false;
    const entries = Array.isArray(serverErrors)
      ? serverErrors.map((e) => [e.Field || e.field || e.PropertyName, e.Message || e.message])
      : Object.entries(serverErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]);
    const mapped = {};
    entries.forEach(([key, msg]) => {
      const field = Object.keys(EMPTY_FORM).find(
        (f) => f.toLowerCase() === String(key || "").toLowerCase(),
      );
      if (field && msg) mapped[field] = msg;
    });
    if (!Object.keys(mapped).length) return false;
    setFormErrors((p) => ({ ...p, ...mapped }));
    return true;
  };

  // ---------- open / close ----------
  const defaultStatusId = () => {
    const open = arrearStatusList.find(
      (s) => String(s.Name).toLowerCase() === "open",
    );
    return String((open || arrearStatusList[0])?.Id ?? "");
  };

  const openAdd = () => {
    setEditArrear(null);
    setFormErrors({});
    setFormData({ ...EMPTY_FORM, ArrearStatusId: defaultStatusId() });
    setShowModal(true);
  };

  const openEdit = (a) => {
    // list rows may carry ids or only names, so resolve either way
    const type =
      arrearTypesList.find((t) => String(t.Id) === String(a.ArrearTypeId)) ||
      arrearTypesList.find((t) => t.Name === a.ArrearTypeName);
    const status =
      arrearStatusList.find((s) => String(s.Id) === String(a.ArrearStatusId)) ||
      arrearStatusList.find((s) => s.Name === a.ArrearStatusName);
    const emp = findEmployee(employeeList, a.EmpId);

    setEditArrear(a);
    setFormErrors({});
    setFormData({
      EmpId: emp ? String(empKey(emp)) : "",
      ArrearTypeId: type ? String(type.Id) : "",
      ForMonth: a.ForMonth || "",
      Amount: a.Amount ?? "",
      ArrearStatusId: status ? String(status.Id) : "",
      Taximpact: a.Taximpact || "Taxable",
      Reason: a.Reason || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!isSaving) setShowModal(false);
  };

  // ---------- CRUD ----------
  const saveArrear = async () => {
    if (isSaving) return;
    if (!(await validateForm())) return;

    const f = formData;
    const params = {
      Id: editArrear?.Id || 0,
      OrgId: userDetails?.OrgId || 0,
      EmpId: Number(f.EmpId),
      ArrearTypeId: Number(f.ArrearTypeId),
      ForMonth: f.ForMonth,
      Amount: Number(f.Amount),
      ArrearStatusId: Number(f.ArrearStatusId),
      Taximpact: f.Taximpact,
      Reason: f.Reason.trim(),
      PaidAmount: editArrear?.PaidAmount ?? 0, // don't wipe what's already paid
    };

    setIsSaving(true);
    try {
      const res = await postArrearUpdate(params);
      if (res?.StatusCode === 2000) {
        toaster?.current?.success(
          `Arrear ${editArrear ? "updated" : "added"} successfully`,
        );
        setShowModal(false);
        if (editArrear) {
          fetchArrears(currentPage(), rows);
        } else {
          setFirst(0);
          fetchArrears(1, rows);
        }
      } else {
        const shown = applyServerErrors(res);
        toaster?.current?.error(
          res?.Message ||
            (shown ? "Please fix the highlighted fields." : "Error saving arrear. Please try again."),
        );
      }
    } catch (error) {
      console.error("Error saving arrear:", error);
      toaster?.current?.error("Error saving arrear. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const askDelete = (a) => {
    setDeleteArrear(a);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteArrear || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await postArrearDelete({ RequestId: deleteArrear.Id });
      if (res?.StatusCode === 2000) {
        toaster?.current?.success("Arrear deleted successfully");
        setShowDeleteModal(false);
        setDeleteArrear(null);
        let page = currentPage();
        if (arrears.length === 1 && page > 1) {
          page -= 1;
          setFirst((page - 1) * rows);
        }
        fetchArrears(page, rows);
      } else {
        toaster?.current?.error(res?.Message || "Error deleting arrear");
      }
    } catch (error) {
      console.error("Error deleting arrear:", error);
      toaster?.current?.error("Error deleting arrear. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------- export (current page) ----------
  const exportToCSV = () => {
    if (!arrears.length) {
      toaster?.current?.error("No data to export.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Employee", "Employee ID", "Arrear Type", "Month", "Amount", "Status", "Tax", "Notes"];
    const body = arrears.map((a) => [
      a.EmployeeName, a.EmpId, a.ArrearTypeName, a.ForMonth,
      a.Amount, a.ArrearStatusName, a.Taximpact, a.Reason,
    ]);
    const csv = [headers, ...body].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "arrears_data.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const pageTotal = useMemo(
    () => arrears.reduce((sum, a) => sum + (a.Amount || 0), 0),
    [arrears],
  );

  const statusClassFor = (name) => {
    const n = String(name).toLowerCase();
    if (n === "open") return "badge-pending";
    if (n === "paid") return "badge-paid";
    return "badge-locked";
  };

  // ---------- render ----------
  return (
    <div className="container-fluid">
      <div className="form-head d-flex flex-wrap align-items-center page-head mb-0">
        <div className="me-auto">
          <h2 className="font-w600 mb-0">Arrear Management</h2>
          <p>
            Salary arrears from revision, backdated increments or missed
            payments. Open/partial records flow into payroll Step 4.
          </p>
        </div>
        <div className="rightside-tabs">
          <div className="topbar-actions">
            <button className="btn-og btn-og-secondary btn-og-sm" onClick={exportToCSV}>
              Export
            </button>
            <button className="btn-og theme-color btn-og-sm" onClick={openAdd}>
              Add Arrear
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* SUMMARY */}
        <div className="arrear-summarycards">
          {[
            ["Open Arrears", summary?.OpenArrears ?? 0, false],
            ["Total Arrear Amount", summary?.TotalArrearAmount ?? 0, true],
            ["Paid Out", summary?.PaidOut ?? 0, true],
            ["This Month Payable", summary?.ThisMonthPayable ?? 0, true],
          ].map(([label, value, money]) => (
            <div className="blocks" key={label}>
              <div className="block-div1">{label}</div>
              <div className="block-div2">{money ? rup(value) : value}</div>
            </div>
          ))}
        </div>

        {/* TABLE */}
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="toolbar filter-row pt-2">
            <input
              type="text"
              className="fs-select"
              placeholder="Search employee…"
              style={{ width: "200px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="toolbar-sep"></div>
            <select
              className="fs-select"
              style={{ width: "160px" }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {arrearTypesList.map((t) => (
                <option key={t.Id} value={t.Id}>{t.Name}</option>
              ))}
            </select>
            <select
              className="fs-select"
              style={{ width: "130px" }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              {arrearStatusList.map((s) => (
                <option key={s.Id} value={s.Id}>{s.Name}</option>
              ))}
            </select>
            <div className="toolbar-spacer"></div>
            <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
              {totalRecords} records
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="mgmt-tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Arrear Type</th>
                  <th>For Month</th>
                  <th className="tr">Amount</th>
                  <th>Reason / Notes</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "24px" }}>
                      Loading arrears…
                    </td>
                  </tr>
                )}
                {!isLoading && arrears.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "24px" }}>
                      No arrears found. Change the filters or add an arrear.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  arrears.map((a) => (
                    <tr key={a.Id}>
                      <td>
                        <span style={{ fontWeight: 600 }}>{a.EmployeeName}</span>
                        <br />
                        <code style={{ fontSize: "10px", color: "var(--brand)" }}>
                          {a.EmployeeCode || a.EmpId}
                        </code>
                      </td>
                      <td>
                        <span className="badge badge-account" style={{ fontSize: "10.5px" }}>
                          {a.ArrearTypeName}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px", color: "var(--text-muted)" }}>{a.ForMonth}</td>
                      <td className="tr" style={{ fontWeight: 700 }}>{rup(a.Amount)}</td>
                      <td style={{ fontSize: "12px", color: "var(--text-muted)", maxWidth: "220px" }}>
                        {a.Reason || "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span className={`badge ${statusClassFor(a.ArrearStatusName)}`}>
                          {a.ArrearStatusName}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button className="btn btn-light btn-sm" onClick={() => openEdit(a)} title="Edit">
                            <i className="fa-solid fa-pen" style={{ fontSize: "10px" }}></i>
                          </button>
                          <button
                            className="btn btn-light btn-sm"
                            onClick={() => askDelete(a)}
                            title="Delete"
                            style={{ color: "var(--danger)" }}
                          >
                            <i className="fa-solid fa-trash" style={{ fontSize: "10px" }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3">PAGE TOTAL</td>
                  <td className="tr" style={{ fontWeight: 700 }}>{rup(pageTotal)}</td>
                  <td colSpan="3">—</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={onPageChange}
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          />
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="mo open" onClick={closeModal}>
          <div className="mb" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              <div
                style={{ width: "30px", height: "30px", borderRadius: "8px", background: "var(--brand-soft)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--brand)" }}
              >
                <i className="pi pi-refresh" style={{ fontSize: "16px" }}></i>
              </div>
              <h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700 }}>
                {editArrear ? "Edit Arrear" : "Add Arrear"}
              </h6>
              <button
                style={{ marginLeft: "auto", background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#888", lineHeight: 1 }}
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="mbody">
              <div className="fsec">Employee &amp; Arrear Details</div>
              <div className="fg2">
                <div>
                  <label className="form-label">
                    Employee <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="EmpId"
                    value={formData.EmpId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.EmpId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {employeeList.map((emp) => (
                      <option key={emp.Id} value={empKey(emp)}>
                        {emp.FullName} ({emp.EmploymentId})
                      </option>
                    ))}
                  </select>
                  <FieldError message={formErrors.EmpId} />
                </div>

                <div>
                  <label className="form-label">
                    Arrear Type <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="ArrearTypeId"
                    value={formData.ArrearTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.ArrearTypeId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {arrearTypesList.map((t) => (
                      <option key={t.Id} value={t.Id}>{t.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.ArrearTypeId} />
                </div>

                <div>
                  <label className="form-label">
                    For Month <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="ForMonth"
                    value={formData.ForMonth}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.ForMonth ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {formonthList.map((m) => (
                      <option key={m.Id} value={m.Name}>{m.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.ForMonth} />
                </div>

                <div>
                  <label className="form-label">
                    Amount (₹) <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control w-100 ${formErrors.Amount ? "is-invalid" : ""}`}
                    name="Amount"
                    min="1"
                    step="0.01"
                    placeholder="0"
                    value={formData.Amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.Amount} />
                </div>

                <div>
                  <label className="form-label">
                    Status <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="ArrearStatusId"
                    value={formData.ArrearStatusId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.ArrearStatusId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {arrearStatusList.map((s) => (
                      <option key={s.Id} value={s.Id}>{s.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.ArrearStatusId} />
                </div>

                <div>
                  <label className="form-label">
                    Tax Impact <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="Taximpact"
                    value={formData.Taximpact}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.Taximpact ? { borderColor: "var(--danger)" } : {}}
                  >
                    {TAX_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.Taximpact} />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Reason / Notes</label>
                  <textarea
                    className="form-control w-100"
                    name="Reason"
                    rows="2"
                    maxLength="500"
                    placeholder="Reason for arrear, HR approval ref…"
                    style={{ resize: "vertical" }}
                    value={formData.Reason}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.Reason} />
                </div>
              </div>
            </div>

            <div className="mfoot">
              <button className="btn btn-light" onClick={closeModal} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn btn-brand" onClick={saveArrear} disabled={isSaving}>
                <i className="pi pi-check" style={{ marginRight: "6px" }}></i>
                {isSaving ? "Saving…" : editArrear ? "Save Changes" : "Add Arrear"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {showDeleteModal && deleteArrear && (
        <div className="mo open" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div
            style={{ background: "#fff", borderRadius: "12px", width: "400px", padding: "24px", boxShadow: "0 24px 64px rgba(0,0,0,.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div style={{ width: "34px", height: "34px", background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--danger)" }}>
                <i className="pi pi-trash" style={{ fontSize: "18px" }}></i>
              </div>
              <h6 style={{ margin: 0, fontWeight: 700 }}>Delete Arrear</h6>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-mid)", margin: "0 0 18px" }}>
              Delete the {deleteArrear.ArrearTypeName} arrear of {rup(deleteArrear.Amount)} for{" "}
              {deleteArrear.EmployeeName}?
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button className="btn btn-light" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                Cancel
              </button>
              <button
                className="btn btn-brand"
                style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArrearManagement;
